import React, { createContext, useContext, useState, useEffect } from 'react';
import { scriptsData, articlesData } from '../data/initialData';
import ApiService from '../services/api';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [scripts, setScripts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [tools, setTools] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalVisits: 0,
    totalDownloads: 0,
    pageViews: {},
    scriptDownloads: {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      console.log('Loading data from API...');
      const [scriptsRes, articlesRes, faqsRes, toolsRes] = await Promise.all([
        ApiService.getScripts(),
        ApiService.getArticles(),
        ApiService.getFaqs(),
        ApiService.getTools()
      ]);
      
      console.log('Scripts from API:', scriptsRes);
      console.log('Articles from API:', articlesRes);
      console.log('FAQs from API:', faqsRes);
      console.log('Tools from API:', toolsRes);
      
      setScripts(Array.isArray(scriptsRes) ? scriptsRes : []);
      setArticles(Array.isArray(articlesRes) ? articlesRes : []);
      setFaqs(Array.isArray(faqsRes) ? faqsRes : []);
      setTools(Array.isArray(toolsRes) ? toolsRes : []);
      
      // Load analytics from localStorage as fallback
      const savedAnalytics = localStorage.getItem('lysh_analytics');
      if (savedAnalytics) {
        setAnalytics(JSON.parse(savedAnalytics));
      }
    } catch (error) {
      console.error('Failed to load data from API:', error);
      // No fallback - show empty arrays if API fails
      setScripts([]);
      setArticles([]);
      setFaqs([]);
      setTools([]);
    }
  };

  const saveScripts = (newScripts) => {
    setScripts(newScripts);
    localStorage.setItem('lysh_scripts', JSON.stringify(newScripts));
  };

  const saveArticles = (newArticles) => {
    setArticles(newArticles);
    localStorage.setItem('lysh_articles', JSON.stringify(newArticles));
  };

  const saveFaqs = (newFaqs) => {
    setFaqs(newFaqs);
    localStorage.setItem('lysh_faqs', JSON.stringify(newFaqs));
  };

  const saveUsers = (newUsers) => {
    setUsers(newUsers);
    localStorage.setItem('lysh_users', JSON.stringify(newUsers));
  };

  const saveAnalytics = (newAnalytics) => {
    setAnalytics(newAnalytics);
    localStorage.setItem('lysh_analytics', JSON.stringify(newAnalytics));
  };

  const addScript = async (script, authorId = null) => {
    try {
      const result = await ApiService.createScript({ ...script, author_id: authorId });
      await loadData(); // Reload data
      return result;
    } catch (error) {
      console.error('Failed to add script:', error);
      throw error;
    }
  };

  const addArticle = async (article, authorId = null) => {
    try {
      const result = await ApiService.createArticle({ ...article, author_id: authorId });
      await loadData(); // Reload data
      return result;
    } catch (error) {
      console.error('Failed to add article:', error);
      throw error;
    }
  };

  const addFaq = async (faq, authorId = null) => {
    try {
      const result = await ApiService.createFaq({ ...faq, author_id: authorId });
      await loadData(); // Reload data
      return result;
    } catch (error) {
      console.error('Failed to add FAQ:', error);
      throw error;
    }
  };

  const addTool = async (tool, authorId = null) => {
    try {
      const result = await ApiService.createTool({ ...tool, author_id: authorId });
      await loadData(); // Reload data
      return result;
    } catch (error) {
      console.error('Failed to add tool:', error);
      throw error;
    }
  };

  const updateScript = async (id, updatedScript) => {
    try {
      await ApiService.updateScript(id, updatedScript);
      await loadData(); // Reload data
    } catch (error) {
      console.error('Failed to update script:', error);
      throw error;
    }
  };

  const updateArticle = async (id, updatedArticle) => {
    try {
      await ApiService.updateArticle(id, updatedArticle);
      await loadData(); // Reload data
    } catch (error) {
      console.error('Failed to update article:', error);
      throw error;
    }
  };

  const updateFaq = async (id, updatedFaq) => {
    try {
      await ApiService.updateFaq(id, updatedFaq);
      await loadData(); // Reload data
    } catch (error) {
      console.error('Failed to update FAQ:', error);
      throw error;
    }
  };

  const updateTool = async (id, updatedTool) => {
    try {
      await ApiService.updateTool(id, updatedTool);
      await loadData(); // Reload data
    } catch (error) {
      console.error('Failed to update tool:', error);
      throw error;
    }
  };

  const deleteScript = async (id) => {
    try {
      const result = await ApiService.deleteScript(id);
      if (result) {
        const newScripts = scripts.filter(script => script.id !== id);
        setScripts(newScripts);
      }
    } catch (error) {
      console.error('Failed to delete script:', error);
      // Reload data to ensure consistency
      await loadData();
      throw error;
    }
  };

  const deleteArticle = async (id) => {
    try {
      const result = await ApiService.deleteArticle(id);
      if (result) {
        const newArticles = articles.filter(article => article.id !== id);
        setArticles(newArticles);
      }
    } catch (error) {
      console.error('Failed to delete article:', error);
      // Reload data to ensure consistency
      await loadData();
      throw error;
    }
  };

  const deleteFaq = async (id) => {
    try {
      const result = await ApiService.deleteFaq(id);
      if (result) {
        const newFaqs = faqs.filter(faq => faq.id !== id);
        setFaqs(newFaqs);
      }
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
      // Reload data to ensure consistency
      await loadData();
      throw error;
    }
  };

  const deleteTool = async (id) => {
    try {
      const result = await ApiService.deleteTool(id);
      if (result) {
        const newTools = tools.filter(tool => tool.id !== id);
        setTools(newTools);
      }
    } catch (error) {
      console.error('Failed to delete tool:', error);
      await loadData();
      throw error;
    }
  };

  const addUser = async (user) => {
    try {
      const result = await ApiService.register({
        username: user.username,
        email: user.email,
        password: user.password,
        fullName: user.name
      });
      if (result.success) {
        const newUser = {
          ...user,
          id: Date.now(),
          createdAt: new Date().toISOString()
        };
        const newUsers = [...users, newUser];
        saveUsers(newUsers);
        return newUser;
      }
    } catch (error) {
      console.error('Failed to register user:', error);
      throw error;
    }
  };

  const updateUser = (id, updatedUser) => {
    const newUsers = users.map(user => 
      user.id === id ? { ...user, ...updatedUser } : user
    );
    saveUsers(newUsers);
  };

  const deleteUser = (id) => {
    const newUsers = users.filter(user => user.id !== id);
    saveUsers(newUsers);
  };

  const getUserById = (id) => {
    return users.find(user => user.id === id);
  };

  const trackPageView = async (page) => {
    const sessionKey = `visited_${page}`;
    const hasVisited = sessionStorage.getItem(sessionKey);
    
    if (!hasVisited) {
      sessionStorage.setItem(sessionKey, 'true');
      try {
        await ApiService.trackPageView(page);
      } catch (error) {
        console.error('Failed to track page view:', error);
      }
      const newAnalytics = {
        ...analytics,
        totalVisits: analytics.totalVisits + 1,
        pageViews: {
          ...analytics.pageViews,
          [page]: (analytics.pageViews[page] || 0) + 1
        }
      };
      saveAnalytics(newAnalytics);
    }
  };

  const trackDownload = async (scriptId) => {
    try {
      await ApiService.trackDownload(scriptId);
      const newAnalytics = {
        ...analytics,
        totalDownloads: analytics.totalDownloads + 1,
        scriptDownloads: {
          ...analytics.scriptDownloads,
          [scriptId]: (analytics.scriptDownloads[scriptId] || 0) + 1
        }
      };
      saveAnalytics(newAnalytics);
    } catch (error) {
      console.error('Failed to track download:', error);
    }
  };

  const searchContent = (query, type = 'all') => {
    if (!query.trim()) {
      if (type === 'scripts') return scripts;
      if (type === 'articles') return articles;
      if (type === 'faqs') return faqs;
      if (type === 'tools') return tools;
      return [...scripts, ...articles, ...faqs, ...tools];
    }
    
    const lowercaseQuery = query.toLowerCase();
    const searchItems = type === 'scripts' ? scripts : type === 'articles' ? articles : type === 'faqs' ? faqs : type === 'tools' ? tools : [...scripts, ...articles, ...faqs, ...tools];
    
    return searchItems.filter(item => 
      (item.title || '').toLowerCase().includes(lowercaseQuery) ||
      (item.description || '').toLowerCase().includes(lowercaseQuery) ||
      (item.question || '').toLowerCase().includes(lowercaseQuery) ||
      (item.answer || '').toLowerCase().includes(lowercaseQuery) ||
      (item.category || '').toLowerCase().includes(lowercaseQuery) ||
      (item.subcategory || '').toLowerCase().includes(lowercaseQuery) ||
      (item.content || '').toLowerCase().includes(lowercaseQuery)
    );
  };

  const getContentByCategory = (category, subcategory = null, type = 'scripts') => {
    const items = type === 'articles' ? articles : type === 'faqs' ? faqs : type === 'tools' ? tools : scripts;
    return items.filter(item => {
      if (subcategory) {
        return item.category === category && item.subcategory === subcategory;
      }
      return item.category === category;
    });
  };

  const value = {
    scripts,
    articles,
    faqs,
    tools,
    users,
    analytics,
    addScript,
    addArticle,
    addFaq,
    addTool,
    updateScript,
    updateArticle,
    updateFaq,
    updateTool,
    deleteScript,
    deleteArticle,
    deleteFaq,
    deleteTool,
    addUser,
    updateUser,
    deleteUser,
    getUserById,
    trackPageView,
    trackDownload,
    searchContent,
    getContentByCategory
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};